import React from 'react';
import { flowRight } from 'lodash';
import I18n from 'i18n-js';
import track from 'react-tracking';
import gql from 'graphql-tag';
import { withUserContext, IUserContext } from './user_context_provider';
import { isExperimentEnabled } from '../user_context_helpers';
import experiments from '../experiments';
import { useViewTracking } from '../use_tracking';
import { MParticleEventName } from '../elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import {
  ListingsCollection,
} from './listings_collection';
import { mParticleListingFields } from './with_mparticle_listings';
import { CommonsWatchedListings } from '../rql';
import EmptyStateWatchedListings from './empty_state_watched_listings';
import { userIsTrackable } from '../elog/mparticle';
import { withGraphql } from '../with_graphql';
import { ChildProps } from 'react-apollo';

const COMPONENT_NAME = 'WatchRow';
const MAX_COUNT = 12;
const MIN_COUNT = 4;

export interface ExternalProps {
  mainCallToAction?: React.ReactElement;
  secondaryCallToAction?: React.ReactElement;
}

type IProps = ChildProps<IUserContext & ExternalProps, CommonsWatchedListings.Query>;

const withQuery = withGraphql<IProps, CommonsWatchedListings.Query, CommonsWatchedListings.Variables>(
  gql`
    query Commons_WatchedListings(
      $limit: Int
      $showListingLocation: Boolean!
      $shouldSkipTracking: Boolean!
    ) {
      me {
        uuid
        watches(input: {
          limit: $limit
          listingsState: LIVE
        }) {
          nodes {
            _id
            listing {
              _id
              ... ListingsCollection
              ... mParticleListingFields @skip(if: $shouldSkipTracking)
              ... ShopFields @include(if: $showListingLocation)
            }
          }
        }
      }
    }
    ${ListingsCollection.fragments.listing}
    ${ListingsCollection.fragments.shopFields}
    ${mParticleListingFields}
  `,
  {
    options: ownProps => ({
      ssr: true,
      skip: !!ownProps.user?.loggedOut,
      variables: {
        limit: MAX_COUNT,
        showListingLocation: isExperimentEnabled(ownProps.user, experiments.LISTING_CARD_LOCATION),
        shouldSkipTracking: !userIsTrackable(),
      },
    }),
  },
);

export function WatchedListings(props: IProps) {
  const { data: { me, loading }, user } = props;
  const listings = (me?.watches?.nodes ?? []).map(n => n.listing);

  const trackingData = {
    eventName: MParticleEventName.ComponentView,
    componentName: COMPONENT_NAME,
    experiments: formatExperimentsForEventAttributes(user, [experiments.HOMEPAGE_LOGGED_IN_EMPTY_WATCHLIST]),
    listingCount: listings.length,
  };
  useViewTracking(trackingData, (!loading && listings.length < MIN_COUNT));

  const isEmptyWatchlist = !loading && !listings.length;
  if (isExperimentEnabled(user, experiments.HOMEPAGE_LOGGED_IN_EMPTY_WATCHLIST)) {
    if (isEmptyWatchlist) {
      return (
        <EmptyStateWatchedListings
          user={props.user}
          mainCallToAction={props.mainCallToAction}
          secondaryCallToAction={props.secondaryCallToAction}
        />
      );
    }

    return (
      <ListingsCollection
        listings={listings}
        loading={loading}
        callToAction={props.mainCallToAction}
        componentName={COMPONENT_NAME}
        title={I18n.t('commons.watchRow.loggedIn.title')}
        largeTiles
      />
    );
  }

  if (listings.length < MIN_COUNT) { return null; }

  return (
    <ListingsCollection
      listings={listings}
      loading={loading}
      callToAction={props.mainCallToAction}
      componentName={COMPONENT_NAME}
      title={I18n.t('commons.watchRow.loggedIn.title')}
      largeTiles
    />
  );
}

const connected = flowRight([
  withUserContext,
  withQuery,
])(track({ componentName: COMPONENT_NAME })(WatchedListings));

export default connected;
